import React, { useCallback, useEffect, useState, useRef } from 'react';
import { XIcon, ArrowRightIcon, ArrowLeftIcon, SparklesIcon } from 'lucide-react';
export type OnboardingStep = 'feature-hub-nav' | 'new-feature-button' | 'feature-template' | 'persona-hub-nav' | 'new-persona-button' | 'persona-template' | 'test-hub-nav' | 'new-test-button' | 'test-template' | 'welcome' | null;
interface StepConfig {
  targetId: string;
  title: string;
  message: string;
  placement: 'right' | 'bottom' | 'top' | 'left';
}
const STEP_ORDER: OnboardingStep[] = ['feature-hub-nav', 'new-feature-button', 'feature-template', 'persona-hub-nav', 'new-persona-button', 'persona-template', 'test-hub-nav', 'new-test-button', 'test-template', 'welcome'];
const STEP_CONFIGS: Record<NonNullable<Exclude<OnboardingStep, 'welcome'>>, StepConfig> = {
  'feature-hub-nav': {
    targetId: 'onboarding-feature-hub-nav',
    title: 'Feature Hub',
    message: 'Here is where your features will live.',
    placement: 'right'
  },
  'new-feature-button': {
    targetId: 'onboarding-new-feature-btn',
    title: 'Add a Feature',
    message: 'Select "+ New Feature" to add a feature. Try it out!',
    placement: 'bottom'
  },
  'feature-template': {
    targetId: 'onboarding-hub-content',
    title: 'Feature Template',
    message: 'Fill out the feature template to define your product feature.',
    placement: 'top'
  },
  'persona-hub-nav': {
    targetId: 'onboarding-persona-hub-nav',
    title: 'Persona Hub',
    message: 'Here is where your personas will live.',
    placement: 'right'
  },
  'new-persona-button': {
    targetId: 'onboarding-new-persona-btn',
    title: 'Add a Persona',
    message: 'Select "+ New Persona" to add a Persona. Try it out!',
    placement: 'bottom'
  },
  'persona-template': {
    targetId: 'onboarding-hub-content',
    title: 'Persona Template',
    message: 'Fill out the persona template to define your target user.',
    placement: 'top'
  },
  'test-hub-nav': {
    targetId: 'onboarding-test-hub-nav',
    title: 'Test Hub',
    message: 'Here is where your tests will live.',
    placement: 'right'
  },
  'new-test-button': {
    targetId: 'onboarding-new-test-btn',
    title: 'Add a Test',
    message: 'Select "+ New Test" to start a new test.',
    placement: 'bottom'
  },
  'test-template': {
    targetId: 'onboarding-hub-content',
    title: 'Test Template',
    message: 'Fill out the test template to set up your user test.',
    placement: 'top'
  }
};
interface TooltipPosition {
  top: number;
  left: number;
  arrowDir: 'left' | 'right' | 'top' | 'bottom';
}
function getTooltipPosition(targetRect: DOMRect, placement: StepConfig['placement'], tooltipWidth = 300, tooltipHeight = 140): TooltipPosition {
  const GAP = 16;
  if (placement === 'right') {
    return {
      top: Math.max(16, targetRect.top + targetRect.height / 2 - tooltipHeight / 2),
      left: targetRect.right + GAP,
      arrowDir: 'left'
    };
  }
  if (placement === 'left') {
    return {
      top: Math.max(16, targetRect.top + targetRect.height / 2 - tooltipHeight / 2),
      left: Math.max(16, targetRect.left - tooltipWidth - GAP),
      arrowDir: 'right'
    };
  }
  if (placement === 'bottom') {
    return {
      top: targetRect.bottom + GAP,
      left: Math.max(16, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16)),
      arrowDir: 'top'
    };
  }
  // top
  return {
    top: Math.max(16, targetRect.top - tooltipHeight - GAP),
    left: Math.max(16, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16)),
    arrowDir: 'bottom'
  };
}
interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}
interface OnboardingTourProps {
  step: OnboardingStep;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}
export function OnboardingTour({
  step,
  onNext,
  onBack,
  onSkip
}: OnboardingTourProps) {
  const [highlight, setHighlight] = useState<HighlightRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number>();
  const currentIndex = step ? STEP_ORDER.indexOf(step) : -1;
  const config = step && step !== 'welcome' ? STEP_CONFIGS[step as NonNullable<Exclude<OnboardingStep, 'welcome'>>] : null;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === STEP_ORDER.length - 1;
  const updatePosition = useCallback(() => {
    if (!config) return;
    const el = document.getElementById(config.targetId);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const PADDING = 6;
    setHighlight({
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2
    });
    setTooltipPos(getTooltipPosition(rect, config.placement));
    setVisible(true);
  }, [config]);
  useEffect(() => {
    if (!step) {
      setVisible(false);
      return;
    }
    if (step === 'welcome') {
      setVisible(true);
      return;
    }
    setVisible(false);
    const timeout = setTimeout(() => {
      updatePosition();
    }, 120);
    return () => clearTimeout(timeout);
  }, [step, updatePosition]);
  useEffect(() => {
    if (!step || step === 'welcome') return;
    const handler = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [step, updatePosition]);
  if (!step || !visible) return null;
  const TOOLTIP_WIDTH = 300;
  // Welcome modal — centered, no spotlight
  if (step === 'welcome') {
    return <>
        <div className="fixed inset-0" style={{
        zIndex: 9990,
        background: 'rgba(0,0,0,0.55)'
      }} data-id="element-262" />
        <div className="fixed inset-0 flex items-center justify-center" style={{
        zIndex: 9995
      }} data-id="element-263">
          <div className="bg-white rounded-none border border-stone-200 p-8 w-full max-w-sm mx-4 text-center" style={{
          boxShadow: '0 16px 48px rgba(0,0,0,0.22), 0 4px 12px rgba(0,0,0,0.10)'
        }} data-id="element-264">
            <div className="w-12 h-12 rounded-none bg-[#0000ff]/10 flex items-center justify-center mx-auto mb-4" data-id="element-265">
              <SparklesIcon className="w-6 h-6 text-[#0000ff]" data-id="element-266" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2" data-id="element-267">
              All done! Welcome to Archetype.
            </h2>
            <p className="text-sm text-stone-500 leading-relaxed mb-6" data-id="element-268">
              Get started by adding a feature!
            </p>
            <button type="button" onClick={onNext} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#0000ff] hover:bg-[#3333ff] rounded-none transition-colors duration-100" data-id="element-269">
              Start testing!
            </button>
            <button type="button" onClick={onSkip} className="mt-3 w-full px-4 py-2 text-xs text-stone-400 hover:text-stone-600 transition-colors duration-100" data-id="element-270">
              Dismiss
            </button>
          </div>
        </div>
      </>;
  }
  if (!config || !highlight || !tooltipPos) return null;
  const arrowStyle: React.CSSProperties = (() => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0
    };
    if (tooltipPos.arrowDir === 'left') return {
      ...base,
      left: -8,
      top: '50%',
      transform: 'translateY(-50%)',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid white',
      filter: 'drop-shadow(-2px 0 2px rgba(0,0,0,0.08))'
    };
    if (tooltipPos.arrowDir === 'right') return {
      ...base,
      right: -8,
      top: '50%',
      transform: 'translateY(-50%)',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid white',
      filter: 'drop-shadow(2px 0 2px rgba(0,0,0,0.08))'
    };
    if (tooltipPos.arrowDir === 'top') return {
      ...base,
      top: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid white',
      filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.08))'
    };
    return {
      ...base,
      bottom: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid white',
      filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.08))'
    };
  })();
  return <>
      {/* Dark overlay with spotlight cutout */}
      <div className="fixed inset-0 pointer-events-none" style={{
      zIndex: 9990
    }} data-id="element-271">
        <svg width="100%" height="100%" style={{
        position: 'absolute',
        inset: 0
      }} data-id="element-272">
          <defs data-id="element-273">
            <mask id="spotlight-mask" data-id="element-274">
              <rect width="100%" height="100%" fill="white" data-id="element-275" />
              <rect x={highlight.left} y={highlight.top} width={highlight.width} height={highlight.height} rx={8} fill="black" data-id="element-276" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#spotlight-mask)" data-id="element-277" />
        </svg>
        <div style={{
        position: 'absolute',
        top: highlight.top,
        left: highlight.left,
        width: highlight.width,
        height: highlight.height,
        borderRadius: 0,
        boxShadow: '0 0 0 2px #0000ff, 0 0 0 4px rgba(0,0,255,0.2)',
        transition: 'all 0.2s ease'
      }} data-id="element-278" />
      </div>

      {/* Click blocker */}
      <div className="fixed inset-0" style={{
      pointerEvents: 'auto',
      zIndex: 9991
    }} onClick={e => e.stopPropagation()} data-id="element-279" />

      {/* Tooltip */}
      <div style={{
      position: 'fixed',
      top: tooltipPos.top,
      left: tooltipPos.left,
      width: TOOLTIP_WIDTH,
      zIndex: 9995,
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.15s ease'
    }} data-id="element-280">
        <div className="relative bg-white rounded-none border border-stone-200 p-4" style={{
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)'
      }} data-id="element-281">
          <div style={arrowStyle} data-id="element-282" />

          <div className="flex items-center justify-between mb-2" data-id="element-283">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-284">
              Step {currentIndex + 1} of {STEP_ORDER.length}
            </span>
            <button type="button" onClick={onSkip} className="w-5 h-5 flex items-center justify-center rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors duration-100" aria-label="Skip onboarding" data-id="element-285">
              <XIcon className="w-3.5 h-3.5" data-id="element-286" />
            </button>
          </div>

          <div className="flex gap-1 mb-3" data-id="element-287">
            {STEP_ORDER.map((_, i) => <div key={i} className={`h-1 rounded-none transition-all duration-200 ${i === currentIndex ? 'bg-[#0000ff] flex-[2]' : i < currentIndex ? 'bg-stone-300 flex-1' : 'bg-stone-200 flex-1'}`} data-id="element-288" />)}
          </div>

          <h3 className="text-sm font-semibold text-stone-900 mb-1" data-id="element-289">
            {config.title}
          </h3>
          <p className="text-sm text-stone-600 leading-relaxed mb-3" data-id="element-290">
            {config.message}
          </p>

          <div className="flex items-center justify-between" data-id="element-291">
            <button type="button" onClick={onBack} disabled={isFirst} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-none transition-colors duration-100 disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-292">
              <ArrowLeftIcon className="w-3 h-3" data-id="element-293" />
              Back
            </button>
            <button type="button" onClick={onNext} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#0000ff] hover:bg-[#3333ff] rounded-none transition-colors duration-100" data-id="element-294">
              Next
              <ArrowRightIcon className="w-3 h-3" data-id="element-295" />
            </button>
          </div>
        </div>
      </div>
    </>;
}
export { STEP_ORDER };